import { FolderFormatsDto, FolderTemplateFormatId, FormatFoldersDto } from "@common/dto/folder-settings";
import client, { clientTransformed } from '@common/utils/rest';
import { getQueriesAsSearch } from '@common/utils';
import { FolderListDto } from "@common/dto/folder/list-dto";
import { HttpSuccessDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { FieldListDto } from "@common/dto/field/list-dto";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { DatasheetFolderListDto } from "@common/dto/folder/datasheet-list-dto";
import { DatasheetFormatListDto } from "@common/dto/format/datasheet-list-dto";
import { GetTableFieldSubfieldsParams, GetTdsFolderFormats } from "@modules/datasheets/components/datasheet-format-management/datasheet-format-management.types";

enum FolderContext {
    ids = 1,
    pds = 2,
}

export type CreateFormatFolderParams = {
  name: string;
  parentId: number;
  context?: FolderContext;
};

export type FolderFormatConfig = {
  path: string;
  folderFormatsPath?: string;
  deleteFormatPath: string;
  deleteFormatFolderPath: string;
  createFormatFolderPath: string;
  getFormatFolderPath?: string;
  getFolderFormatPath?: string;
  queries?: {
    context?: FolderContext;
    folderId?: FolderListDto["id"];
    parentFolderId?: FolderListDto["id"];
    parentId?: FolderListDto["id"];
    folderName?: string;
    header?: boolean;
    formatId?: number;
    tds?: boolean;
    tdsFolder?: boolean;
  };
};

const FOLDERS_FORMATS_CONFIG: Record<
  keyof FolderTemplateFormatId,
  FolderFormatConfig
> = {
  defaultIdsSsFormatId: {
    path: "",
    deleteFormatPath: "/spreadSheets/formats",
    deleteFormatFolderPath: "/spreadSheets/delete",
    createFormatFolderPath: "/saveSSFolder",
    getFormatFolderPath: "/spreadSheets/formats/folders",
    getFolderFormatPath: "/spreadSheets/formats/",
    queries: {
      context: FolderContext.ids,
    },
  },
  defaultPdsSsFormatId: {
    path: "",
    deleteFormatPath: "/spreadSheets/formats",
    deleteFormatFolderPath: "/spreadSheets/delete",
    createFormatFolderPath: "/saveSSFolder",
    getFormatFolderPath: "/spreadSheets/formats/folders",
    getFolderFormatPath: "/spreadSheets/formats/",
    queries: {
      context: FolderContext.pds,
    },
  },
  defaultIdsFormatId: {
    path: "/ds",
    deleteFormatPath: "/deleteDsFormat",
    deleteFormatFolderPath: "/deleteDsFolder",
    createFormatFolderPath: "/saveDSFolder",
    queries: {
      context: FolderContext.ids,
      tds: false,
      tdsFolder: false,
    },
  },
  defaultPdsFormatId: {
    path: "/ds",
    deleteFormatPath: "/deleteDsFormat",
    deleteFormatFolderPath: "/deleteDsFolder",
    createFormatFolderPath: "/saveDSFolder",
    queries: {
      context: FolderContext.pds,
      tds: false,
      tdsFolder: false,
    },
  },
  defaultSubtextFormatId: {
    deleteFormatPath: "/deleteSubtextFormat",
    deleteFormatFolderPath: "/deleteSubtextFormatFolder",
    createFormatFolderPath: "/saveSubtextFormatFolders",
    path: "/subtext",
  },
  defaultSymbolsFormatId: {
    deleteFormatPath: "/deleteSymbolsFormat",
    deleteFormatFolderPath: "/deleteSymbolsFormatFolder",
    createFormatFolderPath: "/saveSymbolsFormatsFolders",
    path: "/symbols",
  },
  defaultPedDropFormatId: {
    deleteFormatPath: "/deleteDropFieldsFormat",
    deleteFormatFolderPath: "/deleteDropFieldsFormatFolder",
    createFormatFolderPath: "/saveDropFieldFormatFolder",
    path: "/dropfields",
  },
  defaultHeaderFormatId: {
    path: "/headerfooter",
    folderFormatsPath: "/headerfooterformats",
    deleteFormatPath: "/deleteHeaderFooterFormat",
    deleteFormatFolderPath: "/deleteHeaderFooterFormatFolder",
    createFormatFolderPath: "/saveHeaderFooterFormatFolder",
    queries: {
      parentFolderId: 0,
      header: true,
    },
  },
  defaultFooterFormatId: {
    path: "/headerfooter",
    folderFormatsPath: "/headerfooterformats",
    deleteFormatPath: "/deleteHeaderFooterFormat",
    deleteFormatFolderPath: "/deleteHeaderFooterFormatFolder",
    createFormatFolderPath: "/saveHeaderFooterFormatFolder",
    queries: {
      parentFolderId: 0,
      header: false,
    },
  },
};

type FormatIdsOnUpdateFolderWithQuery = keyof Pick<FolderTemplateFormatId,'defaultIdsFormatId' | 'defaultPdsFormatId'>;
type FormatIdsHeaderFooter = keyof Pick<FolderTemplateFormatId, 'defaultHeaderFormatId' | 'defaultFooterFormatId'>;
type FormatIdsOnSpreadSheetUpdateFolder = keyof Pick<FolderTemplateFormatId, 'defaultIdsSsFormatId' | 'defaultPdsSsFormatId'>;

export const getFolderFormats = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const formatSsIds: Array<FormatIdsOnSpreadSheetUpdateFolder> = ['defaultIdsSsFormatId', 'defaultPdsSsFormatId'];
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { folderFormatsPath, path } = requestConfig;
    let allQueries = { ...requestConfig.queries, ...queries };
    const getFolderFormatPath = FOLDERS_FORMATS_CONFIG[type].getFolderFormatPath || '';
    let finalPath = `api/formats${folderFormatsPath || path}${getQueriesAsSearch(allQueries)}`;
    
    if (formatSsIds.includes(type as FormatIdsOnSpreadSheetUpdateFolder) && getFolderFormatPath) {
        allQueries = { ...requestConfig.queries };
        finalPath = `api${getFolderFormatPath}${queries?.folderId}${getQueriesAsSearch(allQueries)}`;
    }

    return client({
        path: finalPath,
        method: 'GET'
    }).then((response: { entity: Array<FolderFormatsDto> }) => response.entity);
}; 

export const getFormatFolders = (type: keyof FolderTemplateFormatId) => {
    const formatSsIds: Array<FormatIdsOnSpreadSheetUpdateFolder> = ['defaultIdsSsFormatId', 'defaultPdsSsFormatId'];
    const { path, queries } = FOLDERS_FORMATS_CONFIG[type];
    const getFormatFolderPath = FOLDERS_FORMATS_CONFIG[type].getFormatFolderPath || '';
    let finalPath = `api/folders/formats${path}${getQueriesAsSearch(queries)}`;
  
    if (formatSsIds.includes(type as FormatIdsOnSpreadSheetUpdateFolder) && getFormatFolderPath) {
        finalPath = `api${getFormatFolderPath}${getQueriesAsSearch(queries)}`;
    }

    return client({
        path: finalPath,
        method: 'GET'
    }).then((response: { entity: Array<FolderListDto> }) => response.entity);
};

export const createFormatFolder = (type: keyof FolderTemplateFormatId, { name, parentId }: CreateFormatFolderParams) => {
    const formatIds: Array<FormatIdsOnUpdateFolderWithQuery> = ['defaultIdsFormatId', 'defaultPdsFormatId'];
    const formatIdsFooterHeader: Array<FormatIdsHeaderFooter>  = ['defaultHeaderFormatId', 'defaultFooterFormatId'];
    const formatSsIds: Array<FormatIdsOnSpreadSheetUpdateFolder> = ['defaultIdsSsFormatId', 'defaultPdsSsFormatId'];
  
    let entity;
    let queries;

    if (formatIds.includes(type as FormatIdsOnUpdateFolderWithQuery)) {
        queries = { folderName: name, parentId }
    } else if (formatIdsFooterHeader.includes(type as FormatIdsHeaderFooter)) {
        entity = { folderName: name, parentId };
    } else {
      entity = { foldername: name, parentid: parentId };
    }

    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { createFormatFolderPath } = requestConfig;
    const { context, tds, ...requiredQueries } = requestConfig.queries || {};
    const allQueries = { fieldContext: context, ...requiredQueries, ...queries };
    let finalPath = `api/folders/formats${createFormatFolderPath}${getQueriesAsSearch(allQueries)}`;
  
    if (formatSsIds.includes(type as FormatIdsOnSpreadSheetUpdateFolder)) {
      entity = { context: type === 'defaultIdsSsFormatId' ? 1 : 2, folderName: name, parentId: parentId };
      finalPath = `api/folders/formats${createFormatFolderPath}`;
    }

    return client({
        path: finalPath,
        method: 'POST',
        entity,
    });
};

export const deleteFormatFolder = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { deleteFormatFolderPath } = requestConfig;
    const allQueries = { ...requestConfig.queries, ...queries };

    const finalPath = `api/folders/formats${deleteFormatFolderPath}${getQueriesAsSearch(allQueries)}`;

    return client({
        path: finalPath,
        method: 'POST',
    });
}; 

export const deleteFormat = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const formatSsIds: Array<FormatIdsOnSpreadSheetUpdateFolder> = ['defaultIdsSsFormatId', 'defaultPdsSsFormatId'];
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { deleteFormatPath } = requestConfig;
    let allQueries = { ...requestConfig.queries, ...queries };

    let finalPath = `api/folders/formats${deleteFormatPath}${getQueriesAsSearch(allQueries)}`;
  
    if (formatSsIds.includes(type as FormatIdsOnSpreadSheetUpdateFolder) && deleteFormatPath) {
      allQueries = { ...requestConfig.queries, ...{reportNumber: queries?.formatId} };
      finalPath = `api${deleteFormatPath}${getQueriesAsSearch(allQueries)}`;
    }

    return client({
        path: finalPath,
        method: 'POST',
    });
};

export const getModuleFolderFields = ({
    searchQuery,
    folderId,
    system,
    contextId
}: {
    searchQuery?: string;
    folderId?: FolderPrimaryListDto['id'];
    system: boolean;
    contextId: number
}): Promise<Array<FieldListDto>> => {
  return clientTransformed({
    path: "api/fieldsResource/load/fields",
    params: {
        searchQuery,
        parentId: folderId,
        contextId,
        system,
    },
    method: "GET",
  }).then((response: HttpSuccessDto<Array<FieldListDto>>) => {
    return response.entity;
  });
};

export type GetFolderFieldsParams = {
    searchQuery?: string; 
    folderId?: FolderPrimaryListDto['id'];
    contextId: number;
    includeTables?: boolean;
    includePDSFields?: boolean;
    includeSubfields?: boolean;
};

export const getFolderFields = ({ searchQuery, folderId, contextId, includeTables, includePDSFields, includeSubfields }: GetFolderFieldsParams): Promise<Array<FieldPrimaryListDto>> => {
  return clientTransformed({
    path: "api/fields",
    params: {
        query: searchQuery,
        folderId,
        conext: contextId,
        includeTables,
        includePDSFields,
        includeSubfields,
    },
    method: "GET",
  }).then((response: HttpSuccessDto<Array<FieldPrimaryListDto>>) => {
    return response.entity;
  });
};

export type GetDatasheetFolderFormats = {
    folderId: DatasheetFolderListDto['folderId'];
    context: DatasheetFolderListDto['fieldContext'];
};

export const getDatasheetFolderFormats = (params: GetDatasheetFolderFormats): Promise<Array<DatasheetFormatListDto>> => {
    return client({
        path: 'api/formats/ds',
        method: 'GET',
        params: params
      }).then((res: HttpSuccessDto<{ data: DatasheetFormatListDto[] }>) => {
        return res.entity;
      });
};


export const getTdsFolderFormats = (params: GetTdsFolderFormats): Promise<Array<DatasheetFormatListDto>> => {
    return client({
        path: 'api/formats/ds/tds',
        method: 'GET',
        params: params
      }).then((res: HttpSuccessDto<{ data: DatasheetFormatListDto[] }>) => {
        return res.entity;
      });
};


export const getTableFieldSubfields = (params: GetTableFieldSubfieldsParams): Promise<Array<FieldPrimaryListDto>> => {
    return clientTransformed({
        path: 'api/fields/table/subfields',
        method: 'GET',
        params: {
            fieldId: params.fieldId,
            context: params.context,
        }
    }).then((res: HttpSuccessDto<Array<FieldPrimaryListDto>>) => {
        // The API returns an array of FieldDto which matches FieldPrimaryListDto structure
        return res.entity || [];
    });
};